import { memo, useEffect, useRef } from 'react';
import { useMantineTheme } from '@mantine/core';

import { Scroller } from '~/domains/design-system/components/Scroller/Scroller';

import type { ObserveMarketGroupRef } from '../../hooks/useMarketGroupVisibility';
import { reportPredictionsWidgetTickerScrolled } from '../../analyticsEvents';

import { PromotedMarketCard } from './PromotedMarketCard';
import { WIDGET_LOCATION } from './constants';
import { useStyles, TRACK_WIDTH_VAR } from './HomePredictionMarkets.styles';

type MarketsCarouselProps = {
  marketGroupIds: string[];
  observeMarketGroup: ObserveMarketGroupRef;
};

// Memoised because useLoadedMarketsPusher re-renders the widget on every card entering or
// leaving the viewport, and GameSectionsContent does not memoise.
export const MarketsCarousel = memo(
  ({ marketGroupIds, observeMarketGroup }: MarketsCarouselProps) => {
    const { classes } = useStyles();
    const theme = useMantineTheme();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const maxIndexRef = useRef(0);
    const cardCount = marketGroupIds.length;

    // Publish the visible track width so the cards can size against it. See TRACK_WIDTH_VAR.
    useEffect(() => {
      const el = scrollerRef.current;
      if (!el) return undefined;

      const publishTrackWidth = () => el.style.setProperty(TRACK_WIDTH_VAR, `${el.clientWidth}px`);
      publishTrackWidth();

      if (typeof ResizeObserver === 'undefined') return undefined;

      const observer = new ResizeObserver(publishTrackWidth);
      observer.observe(el);

      return () => observer.disconnect();
    }, []);

    // Reported on unmount rather than per event, which would flood Segment. Kept apart from the
    // listener effect, whose cleanup re-runs whenever a refetch changes the card count.
    useEffect(
      () => () => {
        if (maxIndexRef.current > 0) {
          reportPredictionsWidgetTickerScrolled({
            location: WIDGET_LOCATION,
            maxIndexReached: maxIndexRef.current,
          });
        }
      },
      []
    );

    // React's onScroll does not bubble, so listen on the scroll container itself.
    useEffect(() => {
      const el = scrollerRef.current;
      if (!el) return undefined;

      const handleScroll = () => {
        const cardStride = el.scrollWidth / cardCount;
        if (!cardStride) return;

        const lastVisible = Math.floor((el.scrollLeft + el.clientWidth) / cardStride) - 1;
        maxIndexRef.current = Math.max(maxIndexRef.current, Math.min(lastVisible, cardCount - 1));
      };

      el.addEventListener('scroll', handleScroll, { passive: true });

      return () => el.removeEventListener('scroll', handleScroll);
    }, [cardCount]);

    return (
      <Scroller ref={scrollerRef} backgroundColor={theme.colors.gray2[0]}>
        {/* MarketCard puts className on its inner box, so the track width needs a wrapper. */}
        {marketGroupIds.map((marketGroupId, index) => (
          <div key={marketGroupId} className={classes.carouselItem}>
            <PromotedMarketCard
              marketGroupId={marketGroupId}
              placement={index === 0 ? 'featured' : 'ticker'}
              observeMarketGroup={observeMarketGroup}
            />
          </div>
        ))}
      </Scroller>
    );
  }
);

MarketsCarousel.displayName = 'MarketsCarousel';
